import React, { createContext, useState } from 'react';

export const AuthContext = createContext('');

// Keys stored in sessionStorage
const STORAGE_KEYS = [
  'token',
  'user_id',
  'login_name',
  'login_fname',
  'login_lname',
  'role_type',
  'department',
  'permissions',
];

/** Helper: write key-value pairs into a Storage object */
const setStorage = (storage, data) => {
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      storage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  });
};

/** Helper: remove all known keys from a Storage object */
const clearStorage = (storage) => {
  STORAGE_KEYS.forEach((key) => storage.removeItem(key));
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!sessionStorage.getItem('token')
  );

  /**
   * Login — called after POST /api/auth/login/ succeeds.
   * @param {object} loginData  The response payload from the backend
   */
  const login = (loginData) => {
    try {
      const mapped = {
        token: loginData.token,
        user_id: loginData.user_id,
        login_name: loginData.login_name || loginData.username,
        login_fname: loginData.login_fname || loginData.first_name,
        login_lname: loginData.login_lname || loginData.last_name,
        role_type: loginData.role_type,
        department: loginData.department,
        permissions: loginData.permissions,
      };

      setStorage(sessionStorage, mapped);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  /**
   * Logout — clears session and resets state.
   * The actual POST /api/auth/logout/ call is done by the caller before invoking this.
   */
  const logout = () => {
    clearStorage(sessionStorage);
    setIsAuthenticated(false);
  };

  /**
   * Check whether the current user has a specific permission.
   * super_admin bypasses all checks.
   *
   * @param {string} module  e.g. "finance", "hr"
   * @param {string} action  e.g. "view", "create", "edit", "delete"
   * @returns {boolean}
   */
  const hasPermission = (module, action = 'view') => {
    if (sessionStorage.getItem('role_type') === 'ops_head') return true;
    try {
      const perms = JSON.parse(sessionStorage.getItem('permissions') || '{}');
      const key = action.startsWith('can_') ? action : `can_${action}`;
      return perms?.[module]?.[key] ?? false;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
